import { Space } from 'antd';
import type { MouseEvent } from 'react';
import type { ColumnType } from 'antd/lib/table';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { DeleteAction, EditAction } from '@/components/table/actions';
import { PERMISSION_DELETE, PERMISSION_OBJ_FARMER, PERMISSION_UPDATE } from '@/constants/permissions';
import { withPermissions } from '@/hocs/permission';
import { farmerStore } from '@/stores';
import { showError } from '@/utils/common';
import { getFormattedYear } from '@/utils/moment';
import { getColumnSearchFields } from '@/components/table/filters';
import { FarmerSourceDocumentType } from '@/types/enums';

const SOURCE_DOCUMENT_TYPE_LABELS: Partial<Record<FarmerSourceDocumentType, string>> = {
  [FarmerSourceDocumentType.RA]: 'Rainforest Alliance',
  [FarmerSourceDocumentType.FT]: 'Fairtrade',
  [FarmerSourceDocumentType.MERIDIA_DOCUMENT]: 'Meridia',
};

const PermissionedEditAction = withPermissions([[PERMISSION_OBJ_FARMER, PERMISSION_UPDATE]])(EditAction);
const PermissionedDeleteAction = withPermissions([[PERMISSION_OBJ_FARMER, PERMISSION_DELETE]])(DeleteAction);

const Actions = (_: any, record: any) => {
  const onDelete = (e: MouseEvent) => {
    e.stopPropagation();
    api.farmers
      .delete(record.id)
      .source.then(() => farmerStore.deleteItem(record.id))
      .catch(showError);
  };

  return (
    <Space onClick={(e) => e.stopPropagation()}>
      <PermissionedEditAction uiSref={{ to: 'base-layout.farmers.edit', params: { farmerId: record.id } }} />
      <PermissionedDeleteAction onConfirm={onDelete} />
    </Space>
  );
};

export const columns: ColumnType<cyrian.api.farmers.Farmer>[] = [
  {
    title: <FormattedMessage id="farmers.id" />,
    dataIndex: 'id',
    defaultSortOrder: 'descend',
    sorter: true,
    width: 80,
  },
  {
    title: <FormattedMessage id="farmers.farmerId" />,
    dataIndex: 'farmerId',
    sorter: true,
    width: 150,
    render: (_: any, record: cyrian.api.farmers.Farmer) => record.farmerId ?? '—',
  },
  {
    title: <FormattedMessage id="farmers.firstName" />,
    dataIndex: 'firstName',
    sorter: true,
    width: 150,
  },
  {
    title: <FormattedMessage id="farmers.lastName" />,
    dataIndex: 'lastName',
    sorter: true,
    width: 150,
  },
  {
    title: <FormattedMessage id="farmers.cooperative" />,
    dataIndex: 'cooperatives',
    ...getColumnSearchFields(),
    width: 200,
    render: (_: any, record: cyrian.api.farmers.Farmer) =>
      record.cooperatives?.length ? record.cooperatives.map((c) => c.name).join(', ') : null,
  },
  {
    title: <FormattedMessage id="farmers.farms" />,
    key: 'farms',
    width: 100,
    render: (_: any, record: cyrian.api.farmers.Farmer) => {
      const count = record.farms?.length ?? 0;
      if (count === 0) return '0';
      return String(count);
    },
  },
  {
    title: <FormattedMessage id="farmers.gender" />,
    dataIndex: 'gender',
    sorter: true,
    width: 100,
  },
  {
    title: <FormattedMessage id="farmers.phone" />,
    dataIndex: 'phone',
    sorter: true,
    width: 150,
  },
  {
    title: <FormattedMessage id="farmers.dob" />,
    dataIndex: 'dob',
    sorter: true,
    render: getFormattedYear,
    width: 120,
  },
  {
    title: <FormattedMessage id="farmers.nationalId" />,
    dataIndex: 'nationalId',
    sorter: true,
    width: 150,
  },
  {
    title: <FormattedMessage id="farmers.sourceDocumentType" />,
    dataIndex: 'sourceDocumentType',
    sorter: true,
    width: 150,
    render: (value: FarmerSourceDocumentType) => SOURCE_DOCUMENT_TYPE_LABELS[value] ?? value,
  },
  {
    title: '',
    render: Actions,
    className: 'actions',
  },
];
